import { useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isOrgConsoleContributor as profileIsOrgConsoleContributor } from "@/lib/orgConsoleRoles";
import type { UserType } from "@/types/database";

export function usePermissions() {
  const { user } = useAuth();
  /** Deactivated staff keep the row for history but must not retain client-side powers (F2L-61). */
  const isDeactivated = Boolean(user?.deactivated_at);
  const user_type = isDeactivated ? null : (user?.user_type ?? null);
  const role_type = isDeactivated ? null : (user?.role_type ?? null);
  const permissions = useMemo(
    () => (isDeactivated ? {} : (user?.permissions ?? {})),
    [isDeactivated, user?.permissions],
  );
  const category_scope = useMemo(
    () => (isDeactivated ? [] : (user?.category_scope ?? [])),
    [isDeactivated, user?.category_scope],
  );

  const hasAdminPermission = useCallback(
    (name: string) => user_type === "admin" && Boolean(permissions[name]),
    [user_type, permissions],
  );

  const hasManagerPermission = useCallback(
    (name: string) =>
      user_type === "orguser" &&
      (role_type === "manager" || role_type === "org_contact") &&
      Boolean(permissions[name]),
    [user_type, role_type, permissions],
  );

  const flags = useMemo(() => {
    const isSuperadmin = user_type === "superadmin";
    const isOrgadmin = user_type === "orgadmin";
    const isManager = user_type === "orguser" && role_type === "manager";
    const isOrgContact = user_type === "orguser" && role_type === "org_contact";
    const isOrgConsoleContributor = !isDeactivated && profileIsOrgConsoleContributor(user);
    const isEmployee = user_type === "orguser" && role_type === "employee";
    return {
      isSuperadmin,
      isOrgadmin,
      isManager,
      isOrgContact,
      isOrgConsoleContributor,
      isEmployee,
    };
  }, [user, user_type, role_type, isDeactivated]);

  return useMemo(
    () => ({
      user_type: user_type as UserType | null,
      role_type,
      permissions,
      category_scope,
      hasAdminPermission,
      hasManagerPermission,
      ...flags,
    }),
    [
      user_type,
      role_type,
      permissions,
      category_scope,
      hasAdminPermission,
      hasManagerPermission,
      flags,
    ],
  );
}
